import asia from '../constants/asia';
import levels from '../constants/levels';
import timeframes from '../constants/timeframes';
import { CentralContactsInfo, Location, TrialDetails } from '../types';

// type
type LevelKey = keyof typeof levels;
type AsiaKey = keyof typeof asia;
type TimeframeKey = keyof typeof timeframes;

// OVERVIEW
export const formatLevels = (levelInt?: number): string => {
	if (
		!levelInt ||
		levelInt > Object.values(levels).reduce((a, b) => a + b, 0)
	) {
		return 'unknown';
	}

	const levelEntries = Object.entries(levels) as [LevelKey, number][];
	const selected = levelEntries.filter(([_, bit]) => (levelInt & bit) !== 0);

	if (selected.length === 0) return 'unknown';
	if (selected.length === 1) return selected[0][0];

	const ranges: string[] = [];
	let rangeStart = selected[0][0];
	let prevValue = selected[0][1];
	let prevKey = selected[0][0];

	for (let i = 1; i < selected.length; i++) {
		const [key, value] = selected[i];

		if (value !== prevValue * 2) {
			if (rangeStart === prevKey) {
				ranges.push(rangeStart);
			} else {
				ranges.push(`${rangeStart}-${prevKey}`);
			}
			rangeStart = key;
		}

		prevValue = value;
		prevKey = key;
	}

	if (rangeStart === prevKey) {
		ranges.push(rangeStart);
	} else {
		ranges.push(`${rangeStart}-${prevKey}`);
	}

	return ranges.length > 0 ? ranges.join(', ').toUpperCase() : 'unknown';
};

export const formatAsia = (asiaInt?: number): string => {
	if (!asiaInt) return 'Unknown';

	const result: string[] = [];

	for (const name in asia) {
		const key = name as AsiaKey;
		if (asiaInt & asia[key]) {
			result.push(key.toUpperCase());
		}
	}

	if (result.length) return result.join(', ');
	else return 'Unknown';
};

export const formatTimeFrame = (timeFrameInt?: number): string => {
	if (!timeFrameInt) return 'Unknown';

	const timeframeLabels: Record<TimeframeKey, string> = {
		subacute: 'Sub-Acute',
		acute: 'Acute',
		chronic: 'Chronic',
		traumatic: 'Traumatic',
		non_traumatic: 'Non-Traumatic',
	};

	const result: string[] = [];

	for (const name in timeframes) {
		const key = name as TimeframeKey;
		if (timeFrameInt & timeframes[key]) {
			result.push(timeframeLabels[key]);
		}
	}

	if (result.length) return result.join(', ');
	else return 'Unknown';
};

export const formatAge = (minAge?: string, maxAge?: string) => {
	const hasMin = minAge && minAge !== 'N/A';
	const hasMax = maxAge && maxAge !== 'N/A';

	if (!hasMin && !hasMax) return 'No Age Restrictions';
	else if (!hasMin && hasMax) return 'Up to ' + maxAge;
	else if (hasMin && !hasMax) return 'From ' + minAge;
	else return `From ${minAge} To ${maxAge}`;
};

// TO INT
export const getLevelIntFromCurate = (
	selectedLevels: Record<LevelKey, boolean>
): number => {
	let result = 0;
	for (const key in selectedLevels) {
		if (selectedLevels[key as LevelKey]) {
			result |= levels[key as LevelKey];
		}
	}
	return result;
};

export const getAsiaIntFromCurate = (
	selectedAsia: Record<AsiaKey, boolean>
): number => {
	let result = 0;
	for (const key in selectedAsia) {
		if (selectedAsia[key as AsiaKey]) {
			result |= asia[key as AsiaKey];
		}
	}
	return result;
};

export const getTimeframeIntFromCurate = (
	selectedTimeframes: Record<TimeframeKey, boolean>
): number => {
	let result = 0;
	for (const key in selectedTimeframes) {
		if (selectedTimeframes[key as TimeframeKey]) {
			result |= timeframes[key as TimeframeKey];
		}
	}
	return result;
};

export function extractCentralEmail({
	centralContacts,
}: {
	centralContacts?: TrialDetails['centralContacts'];
}): CentralContactsInfo {
	const centralWithEmail = centralContacts?.filter((c) => c?.email) || [];

	const [first, ...rest] = centralWithEmail;

	return {
		toName: first?.name || '',
		mailTo: first?.email || '',
		ccTo: rest.map((c) => c.email!).filter(Boolean),
		hasEmailContact: centralWithEmail.length > 0,
	};
}

export function extractApplyEmails({
	centralContacts,
	location,
}: {
	centralContacts: TrialDetails['centralContacts'];
	location: Location;
}): string[] {
	const centralContact = centralContacts?.filter((c) => c?.email) || [];
	const locationContacts =
		location?.contacts?.filter((c) => !!c?.email && c?.role === 'CONTACT') ||
		[];

	const contacts = [...centralContact, ...locationContacts];

	const allEmails = contacts
		.map((contact) => contact?.email)
		.filter(Boolean) as string[];

	return allEmails;
}

export function canApply(
	curated: TrialDetails['curated'],
	centralContacts: TrialDetails['centralContacts'],
	location: Location
): boolean {
	const emails = extractApplyEmails({
		centralContacts: centralContacts,
		location,
	});

	if (curated?.apply) {
		switch (curated?.apply?.type) {
			case 'url':
				return !!curated?.apply?.url;
			case 'email':
				return !!curated?.apply?.emails && curated?.apply?.emails?.length > 0;
			case 'default':
			default:
				return emails?.length > 0;
		}
	}
	return emails?.length > 0;
}

// sanitize
export async function sanitizeTrialDetails(trial: any): Promise<TrialDetails> {
	const { id, status, curated = {}, nct = {} } = trial;

	const {
		identificationModule = {},
		statusModule = {},
		descriptionModule = {},
		eligibilityModule = {},
		designModule = {},
		armsInterventionsModule = {},
		outcomesModule = {},
		conditionsModule = {},
		sponsorCollaboratorsModule = {},
		contactsLocationsModule = {},
		referencesModule = {},
		documents = [],
	} = nct;

	return {
		objectID: id,
		status,

		// identificationModule
		briefTitle: identificationModule?.briefTitle,
		officialTitle: identificationModule?.officialTitle,

		// statusModule
		overallStatus: statusModule?.overallStatus,
		startDateStructDate: statusModule?.startDateStruct?.date,
		startDateStructType: statusModule?.startDateStruct?.type,
		completionDateStructDate: statusModule?.completionDateStruct?.date,
		completionDateStructType: statusModule?.completionDateStruct?.type,
		studyFirstPostDateStructDate: statusModule?.studyFirstPostDateStruct?.date,
		lastUpdatePostDateStructDate: statusModule?.lastUpdatePostDateStruct?.date,

		// descriptionModule
		briefSummary: descriptionModule?.briefSummary,
		detailedDescription: descriptionModule?.detailedDescription,

		// eligibilityModule
		sex: eligibilityModule?.sex,
		minimumAge: eligibilityModule?.minimumAge,
		maximumAge: eligibilityModule?.maximumAge,
		healthyVolunteers: eligibilityModule?.healthyVolunteers,
		eligibilityCriteria: eligibilityModule?.eligibilityCriteria,
		studyPopulation: eligibilityModule?.studyPopulation,
		samplingMethod: eligibilityModule?.samplingMethod,

		// designModule
		studyType: designModule?.studyType,
		phases: designModule?.phases,
		enrollmentInfoCount: designModule?.enrollmentInfo?.count,

		// armsInterventionsModule
		armsInterventionsModule,

		// outcomesModule
		outcomesModule,

		// conditionsModule
		conditions: conditionsModule?.conditions,
		keywords: conditionsModule?.keywords,

		// sponsorCollaboratorsModule
		leadSponsorName: sponsorCollaboratorsModule?.leadSponsor?.name,
		collaborators: sponsorCollaboratorsModule?.collaborators,
		investigatorFullName:
			sponsorCollaboratorsModule?.responsibleParty?.investigatorFullName,
		overallOfficialsName: contactsLocationsModule?.overallOfficials?.[0]?.name,
		investigatorAffiliation:
			sponsorCollaboratorsModule?.responsibleParty?.investigatorAffiliation,
		overallOfficialsAffiliation:
			contactsLocationsModule?.overallOfficials?.[0]?.affiliation,

		// contactsLocationsModule
		locations: contactsLocationsModule?.locations ?? [],
		centralContacts: contactsLocationsModule?.centralContacts ?? [],

		// referencesModule
		references: referencesModule?.references,
		seeAlsoLinks: referencesModule?.seeAlsoLinks,

		// documentSection.largeDocumentModule.largeDocs
		documents,

		// curated
		curated: {
			review: {
				whatIsIt: curated?.review?.whatIsIt ?? '',
				whoCanTakePart: curated?.review?.whoCanTakePart ?? '',
				whatsInvolved: curated?.review?.whatsInvolved ?? '',
				pay: curated?.review?.pay ?? '',
				costs: curated?.review?.costs ?? '',
				regulation: curated?.review?.regulation ?? '',
			},
			patient_population: {
				timeframeInt: curated?.patient_population?.timeframeInt ?? 0,
				levelInt: curated?.patient_population?.levelInt ?? 0,
				asiaInt: curated?.patient_population?.asiaInt ?? 0,
				acute: curated?.patient_population?.acute ?? false,
				chronic: curated?.patient_population?.chronic ?? false,
				subacute: curated?.patient_population?.subacute ?? false,
				traumatic: curated?.patient_population?.traumatic ?? false,
				non_traumatic: curated?.patient_population?.non_traumatic ?? false,
				level: {
					c1: curated?.patient_population?.level?.c1 ?? false,
					c2: curated?.patient_population?.level?.c2 ?? false,
					c3: curated?.patient_population?.level?.c3 ?? false,
					c4: curated?.patient_population?.level?.c4 ?? false,
					c5: curated?.patient_population?.level?.c5 ?? false,
					c6: curated?.patient_population?.level?.c6 ?? false,
					c7: curated?.patient_population?.level?.c7 ?? false,
					c8: curated?.patient_population?.level?.c8 ?? false,
					l1: curated?.patient_population?.level?.l1 ?? false,
					l2: curated?.patient_population?.level?.l2 ?? false,
					l3: curated?.patient_population?.level?.l3 ?? false,
					l4: curated?.patient_population?.level?.l4 ?? false,
					l5: curated?.patient_population?.level?.l5 ?? false,
					s1: curated?.patient_population?.level?.s1 ?? false,
					s2: curated?.patient_population?.level?.s2 ?? false,
					s3: curated?.patient_population?.level?.s3 ?? false,
					s4: curated?.patient_population?.level?.s4 ?? false,
					s5: curated?.patient_population?.level?.s5 ?? false,
					t1: curated?.patient_population?.level?.t1 ?? false,
					t2: curated?.patient_population?.level?.t2 ?? false,
					t3: curated?.patient_population?.level?.t3 ?? false,
					t4: curated?.patient_population?.level?.t4 ?? false,
					t5: curated?.patient_population?.level?.t5 ?? false,
					t6: curated?.patient_population?.level?.t6 ?? false,
					t7: curated?.patient_population?.level?.t7 ?? false,
					t8: curated?.patient_population?.level?.t8 ?? false,
					t9: curated?.patient_population?.level?.t9 ?? false,
					t10: curated?.patient_population?.level?.t10 ?? false,
					t11: curated?.patient_population?.level?.t11 ?? false,
					t12: curated?.patient_population?.level?.t12 ?? false,
				},
				asia: {
					a: curated?.patient_population?.asia?.a ?? false,
					b: curated?.patient_population?.asia?.b ?? false,
					c: curated?.patient_population?.asia?.c ?? false,
					d: curated?.patient_population?.asia?.d ?? false,
				},
			},
			comments: {
				reviewStatus: curated?.comments?.reviewStatus ?? '',
				patientPopulation: curated?.comments?.patientPopulation ?? '',
				asia: curated?.comments?.asia ?? '',
				injuryLevels: curated?.comments?.injuryLevels ?? '',
				what: curated?.comments?.what ?? '',
				who: curated?.comments?.who ?? '',
				required: curated?.comments?.required ?? '',
				pay: curated?.comments?.pay ?? '',
				costs: curated?.comments?.costs ?? '',
				controlGroup: curated?.comments?.controlGroup ?? '',
				regulatory: curated?.comments?.regulatory ?? '',
			},
			verification: {
				verified: curated?.verification?.verified ?? false,
				piHistory: curated?.verification?.piHistory ?? false,
				organisationPreviousHistory:
					curated?.verification?.organisationPreviousHistory ?? false,
				protocolRequired: curated?.verification?.protocolRequired ?? false,
				receivedProtocol: curated?.verification?.receivedProtocol ?? false,
				paymentRequired: curated?.verification?.paymentRequired ?? false,
				notes: curated?.comments?.regulatory ?? '',
			},
			apply: {
				type: curated?.apply?.type ?? 'default',
				url: curated?.apply?.url ?? '',
				emails: curated?.apply?.emails ?? [],
			},
			controlGroup: curated?.controlGroup ?? 'no',
			controlGroupFullTreatment: curated?.controlGroupFullTreatment ?? 'no',
			controlGroupText: curated?.controlGroupText ?? '',
			piAuthorId: curated?.piAuthorId ?? '',
			treatments: curated?.treatments ?? [],
			outcomes: curated?.outcomes ?? [],
		},
	};
}

// NCT00061295
